import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
    explanation: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const SafetyRules: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        setShowExplanation(false);
        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeSocialStudies/SafetyRules.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeSocialStudies/SafetyRules.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setShowExplanation(false);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
        else {
            setShowExplanation(true);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Safety Rules | Class 3 Social Studies (CBSE & ICSE)";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn essential safety rules at home, school, and on the road, plus first aid tips and emergency numbers, in this Class 3 Social Studies chapter for CBSE & ICSE students." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <header>
                                <h1>Chapter 5: Safety Rules</h1>
                            </header>

                            <section>
                                <h2>Introduction</h2>
                                <p>Safety rules are important to protect ourselves and others from accidents and harm. By following these rules at home, school, and on the roads, we can live a safe and healthy life. Knowing about first aid and emergency contacts can also help us during unexpected situations.</p>
                            </section>

                            <section>
                                <h2>Safety at Home</h2>
                                <ul>
                                    <li>Use kitchen appliances like gas stoves, ovens, and mixers only with adult guidance.</li>
                                    <li>Handle sharp objects such as knives, blades, and scissors carefully.</li>
                                    <li>Keep inflammable objects like matchsticks, lighters, and kerosene away from children.</li>
                                    <li>Store toilet cleaners and chemicals in a locked cupboard.</li>
                                    <li>Unplug electronic gadgets like irons, heaters, and hair dryers when not in use.</li>
                                    <li>Use mobiles, tablets, and computers safely, with parental guidance and limited screen time.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Safety at School</h2>
                                <ul>
                                    <li>Do not run or push others in classrooms.</li>
                                    <li>Follow all instructions in laboratories.</li>
                                    <li>Use playgrounds carefully to avoid injuries.</li>
                                    <li>Walk slowly and hold the railing while using staircases.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Safety on the Roads</h2>
                                <ul>
                                    <li>Walk on the footpath and cross roads at zebra crossings.</li>
                                    <li>Look right, left, and right again before crossing.</li>
                                    <li>Wear a helmet while cycling and follow road signs.</li>
                                    <li>Understand traffic symbols – red for stop, yellow for wait, green for go.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>First Aid</h2>
                                <ul>
                                    <li>Wash small cuts and cover them with a clean bandage.</li>
                                    <li>For burns, put the affected area under running water.</li>
                                    <li>Do not move someone with possible broken bones; wait for help.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Emergency Numbers</h2>
                                <div className="emergency">
                                    <p><strong>Police:</strong> 100</p>
                                    <p><strong>Fire brigade:</strong> 101</p>
                                    <p><strong>Ambulance:</strong> 102</p>
                                    <p><strong>Child helpline:</strong> 1098</p>
                                </div>
                            </section>

                            <section>
                                <h2>Good Touch and Bad Touch</h2>
                                <ul>
                                    <li><strong>Good touch:</strong> Makes you feel safe, happy, and cared for – like a hug from parents.</li>
                                    <li><strong>Bad touch:</strong> Makes you feel uncomfortable or scared. Shout “NO” and tell a trusted adult immediately.</li>
                                </ul>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <header>
                            <h1>Class 3 Social Studies – Chapter 5: Safety Rules</h1>
                        </header>

                        <section>
                            <h2>Introduction</h2>
                            <p>Safety rules are important to protect ourselves and others from accidents and harm. By following these rules at home, school, and on the roads, we can live a safe and healthy life. Knowing about first aid and emergency contacts can also help us during unexpected situations.</p>
                        </section>

                        <section>
                            <h2>Safety at Home</h2>
                            <ul>
                                <li>Use kitchen appliances like gas stoves, ovens, and mixers only with adult guidance.</li>
                                <li>Handle sharp objects such as knives, blades, and scissors carefully.</li>
                                <li>Keep inflammable objects like matchsticks, lighters, and kerosene away from children.</li>
                                <li>Store toilet cleaners and chemicals in a locked cupboard.</li>
                                <li>Unplug electronic gadgets like irons, heaters, and hair dryers when not in use.</li>
                                <li>Use mobiles, tablets, and computers safely, with parental guidance and limited screen time.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Safety at School</h2>
                            <ul>
                                <li>Do not run or push others in classrooms.</li>
                                <li>Follow all instructions in laboratories.</li>
                                <li>Use playgrounds carefully to avoid injuries.</li>
                                <li>Walk slowly and hold the railing while using staircases.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Safety on the Roads</h2>
                            <ul>
                                <li>Walk on the footpath and cross roads at zebra crossings.</li>
                                <li>Look right, left, and right again before crossing.</li>
                                <li>Wear a helmet while cycling and follow road signs.</li>
                                <li>Understand traffic symbols – red for stop, yellow for wait, green for go.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>First Aid</h2>
                            <ul>
                                <li>Wash small cuts and cover them with a clean bandage.</li>
                                <li>For burns, put the affected area under running water.</li>
                                <li>Do not move someone with possible broken bones; wait for help.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Emergency Numbers</h2>
                            <div className="emergency">
                                <p><strong>Police:</strong> 100</p>
                                <p><strong>Fire brigade:</strong> 101</p>
                                <p><strong>Ambulance:</strong> 102</p>
                                <p><strong>Child helpline:</strong> 1098</p>
                            </div>
                        </section>

                        <section>
                            <h2>Good Touch and Bad Touch</h2>
                            <ul>
                                <li><strong>Good touch:</strong> Makes you feel safe, happy, and cared for – like a hug from parents.</li>
                                <li><strong>Bad touch:</strong> Makes you feel uncomfortable or scared. Shout “NO” and tell a trusted adult immediately.</li>
                            </ul>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}

                            {(showExplanation) ? (
                                <>
                                    <span><strong>Correct option:</strong> {currentQuestion.answer}</span>
                                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}>
                                    </div>
                                    <div className="mt-3 text-center">
                                        <button className="btn hero-btn" onClick={loadRandomSyllabusQuestions}>Next Question</button>
                                    </div>
                                </>
                            ) : (<></>)}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default SafetyRules;
